import {useState, useEffect} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import useMediaQuery from '@mui/material/useMediaQuery'

import {connect, useDispatch} from 'react-redux'

import {shuffle} from 'lodash'

import {usePlayButtonState} from '../contexts/PlayButtonContext'


import {
  convertVideoToTrackFormat, 
  convertAppleMusicLinkToTrackForEvent
} from '../../helpers'

import {play, pause, queue as queueTracks, enqueue} from '../../actions'

import {selectTracksByIDs, makeSelectSearchPerformances} from '../../selectors'

import Zoom from '@mui/material/Zoom'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import PlayIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'





const styles = {
  tooltipButtonContainer: css`
    width: 100%;
    display: flex;
    justify-content: flex-end;
  `,
  playButtonContainer: css`
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    width: 60px;
    height: 60px;
    z-index: 10;
    position: absolute;
    top: 5px;
    left: 5px;
    background: rgb(255, 0, 149);
    box-shadow: 0px 5px 10px -3px rgba(255,0,85,0.55);
    padding: 20px;
    cursor: pointer;

    @media (max-width: 600px) {
      width: 50px;
      height: 50px;
      padding: 10px;
      top: 65px;
      right: 5px;
    }
  `,
}



const tip = `
Clicking this button will play a mix of music from artists that are playing 
near you. To update what gets played when you click
this button, uddate your search criteria.
`



// Hitting the play button will show:
// - Tracks from the performers at the events that are displyed on the map
// - Videos that are saved on the events
// - Spotify tracks from the events
// 
// Doing a search refreshes this list
const MapPlayButton = props => {

  const dispatch = useDispatch()

  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'))


  const {
    mapQueue,
    setMapQueue,
    registeredLinks,
    registeredEvents,
    setRegisteredLinks,
    setRegisteredEvents
  } = usePlayButtonState()



  const {
    play, 
    queue,
    tracks, 
    events,
    enqueue,
    isPlaying,
    callerType,
    queueTracks,
  } = props



  const [isTooltipViewed, setIsTooltipViewed] = useState(
    Boolean(window.localStorage.getItem('isPlayButtonTooltipViewed'))
  )



  useEffect(() => {
    const button = document.getElementById('map-play-button')

    if (button) {
      if (isLargeScreen) {
        button.style.top = '5px'
        button.style.left = '5px'
        button.style.right = null
        button.style.bottom = null
      } else {
        button.style.top = '65px'
        button.style.right = '5px'
        button.style.left = null
        button.style.bottom = null
      }
    }
  }, [isLargeScreen])




  // Identify spotifyLinks and videos that should be created to tracks for the media player
  useEffect(() => {
    let newTracks = []
    let shouldUpdatePlayButtonState = false


    for (const event of Object.values(events)) {
      const videoLink = event.attributes.video
      const appleMusicLinks = event.attributes.appleMusicLinks


      // If this event has been accounted for then go to the next
      if (registeredEvents.has(event.id)) {
        continue
      }


      registeredEvents.add(event.id)
      shouldUpdatePlayButtonState = true


      if (videoLink && !registeredLinks.has(videoLink)) {
        const track = convertVideoToTrackFormat(videoLink, event.attributes.name, event.id, 'Performance')

        newTracks.push(track)
        registeredLinks.add(videoLink)
      }


      if (appleMusicLinks.length) {
        for (const link of appleMusicLinks) {
          if (registeredLinks.has(link)) {
            continue
          }

          const track = convertAppleMusicLinkToTrackForEvent(link, event)


          newTracks.push(track)
          registeredLinks.add(link)
        }
      }
    }


    if (newTracks.length > 0) {
      setMapQueue(prev => [...prev, ...newTracks])

      if (callerType === 'map') {
        enqueue(newTracks, 'map', 'map')
      }
    }


    if (shouldUpdatePlayButtonState) {
      setRegisteredLinks(new Set(registeredLinks))
      setRegisteredEvents(new Set(registeredEvents))
    }
  }, [
    events,
    enqueue,
    dispatch,
    callerType,
    setMapQueue,
    registeredLinks,
    registeredEvents,
    setRegisteredLinks,
    setRegisteredEvents
  ])



  function handleClick() {
    if (isPlaying) {
      pause()
      return
    }


    if (callerType === 'map' && queue.length) {
      play()
      return
    }
    
    queueTracks(shuffle(mapQueue), 'map', 'map', 'map')
    play()
  }



  function markTooltipAsViewed(e) {
    e.stopPropagation()

    setIsTooltipViewed(true)
    window.localStorage.setItem('isPlayButtonTooltipViewed', true)
  }



  return (
    <>
      {mapQueue.length > 0 &&
        <div id='map-play-button' css={styles.playButtonContainer} onClick={handleClick}>
          {isTooltipViewed &&
            <>
              {isPlaying 
                ? <PauseIcon color='secondary' />
                : <PlayIcon  color='secondary' />
              }
            </>
          }

          {!isTooltipViewed &&
            <Tooltip 
              title={
                <div>
                  {tip}

                  <div css={styles.tooltipButtonContainer}>
                    <Button variant='text' color='secondary' onClick={markTooltipAsViewed}>
                      Got it
                    </Button>
                  </div>
                </div>
              }
              TransitionComponent={Zoom}
            >
              <PlayIcon color='secondary' />
            </Tooltip>
          }
        </div>
      }
    </>
  )
}








const makeMapStateToProps = () => {
  const getEvents = makeSelectSearchPerformances()
  const getTracksByIDs = selectTracksByIDs()


  const mapStateToProps = state => {
    const events = getEvents(state)

    const trackIDs = state.search.tracks.results

    const callerType = state.player.callerType


    return {
      queue: state.player.queue,
      events: events,
      tracks: getTracksByIDs(state, trackIDs),
      callerType: callerType,
      isPlaying: callerType === 'map' && state.player.isPlayRequested
    }
  }

  return mapStateToProps
}


const actions = {play, pause, queueTracks, enqueue}


export default connect(makeMapStateToProps, actions)(MapPlayButton)


